import { useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { Mail, User, Phone, MessageSquare, Tag, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BRAND } from "@/constants/brand";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_REGEX = /^[+]?[\d\s\-()]{7,15}$/;

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

// Module-scope Field component (kept OUTSIDE ContactForm to prevent remount / focus loss on every keystroke)
const Field = ({
  id,
  name,
  label,
  type = "text",
  icon: Icon,
  placeholder,
  textarea = false,
  value,
  error,
  onChange,
}) => (
  <div>
    <label
      htmlFor={id}
      className="mb-1.5 block text-xs font-semibold uppercase tracking-widest text-muted-foreground"
    >
      {label}
    </label>
    <div
      className={`relative flex items-start gap-3 rounded-xl border bg-background/40 px-4 py-3 ${error ? "border-destructive/60" : "border-border"
        }`}
    >
      <Icon size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
      {textarea ? (
        <textarea
          id={id}
          name={name}
          rows={5}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          data-testid={`contact-input-${name}`}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none"
          data-testid={`contact-input-${name}`}
        />
      )}
    </div>
    {error ? (
      <p
        className="mt-1.5 flex items-center gap-1 text-xs text-destructive"
        data-testid={`contact-error-${name}`}
      >
        <AlertCircle size={12} /> {error}
      </p>
    ) : null}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2) e.name = "Please enter your name.";
    if (!EMAIL_REGEX.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (!PHONE_REGEX.test(form.phone.trim())) e.phone = "Enter a valid phone number.";
    if (!form.subject.trim() || form.subject.trim().length < 3) e.subject = "Subject is too short.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Message should be at least 10 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (evt) => {
    evt.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      setStatus("error");
      toast.error("Email service not configured yet. Please try again shortly.");
      return;
    }

    setStatus("loading");
    try {
      const templateParams = {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        subject: form.subject,
        message: form.message,
        to_name: BRAND.name,
        reply_to: form.email,
        contact_email: process.env.REACT_APP_CONTACT_EMAIL || "",
      };
      await emailjs.send(serviceId, templateId, templateParams, { publicKey });
      setStatus("success");
      setForm(initialForm);
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="contact" className="section">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          {/* Left copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <span className="chip" data-testid="contact-badge">Business Enquiry</span>
            <h2
              className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl"
              data-testid="contact-heading"
            >
              Let&apos;s{" "}
              <span className="text-gradient-brand">talk business.</span>
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Collaborations, sponsorships, brand partnerships or a quick hello — drop a message and I&apos;ll respond personally.
            </p>

            <div className="mt-8 space-y-4">
              <div className="glass flex items-center gap-4 rounded-2xl p-4">
                <div className="rounded-xl bg-[hsl(var(--brand-orange)/0.15)] p-3 text-[hsl(var(--brand-orange))]">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Reach out via</p>
                  <p className="font-display text-base font-semibold">The form on the right</p>
                </div>
              </div>
              <div className="glass flex items-center gap-4 rounded-2xl p-4">
                <div className="rounded-xl bg-[hsl(var(--brand-green)/0.15)] p-3 text-[hsl(var(--brand-green))]">
                  <MessageSquare size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Response time</p>
                  <p className="font-display text-base font-semibold">Usually within 24 hours</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="glass rounded-3xl p-6 md:p-8 lg:col-span-7"
            data-testid="contact-form"
            noValidate
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <Field
                id="cf-name"
                name="name"
                label="Your name"
                icon={User}
                placeholder="e.g. Rahul Sharma"
                value={form.name}
                error={errors.name}
                onChange={onChange}
              />
              <Field
                id="cf-email"
                name="email"
                label="Email"
                type="email"
                icon={Mail}
                placeholder="you@example.com"
                value={form.email}
                error={errors.email}
                onChange={onChange}
              />
              <Field
                id="cf-phone"
                name="phone"
                label="Phone"
                type="tel"
                icon={Phone}
                placeholder="+91 98765 43210"
                value={form.phone}
                error={errors.phone}
                onChange={onChange}
              />
              <Field
                id="cf-subject"
                name="subject"
                label="Subject"
                icon={Tag}
                placeholder="Brand collaboration"
                value={form.subject}
                error={errors.subject}
                onChange={onChange}
              />
            </div>
            <div className="mt-5">
              <Field
                id="cf-message"
                name="message"
                label="Message"
                icon={MessageSquare}
                placeholder="Tell us about your enquiry..."
                textarea
                value={form.message}
                error={errors.message}
                onChange={onChange}
              />
            </div>

            <div className="mt-6 flex flex-col-reverse items-start justify-between gap-4 sm:flex-row sm:items-center">
              <p className="text-xs text-muted-foreground">
                By submitting, you agree to be contacted at the details above.
              </p>
              <button
                type="submit"
                className="btn-brand min-w-[180px]"
                disabled={status === "loading"}
                data-testid="contact-submit-btn"
              >
                {status === "loading" ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : status === "success" ? (
                  <>
                    <CheckCircle2 size={18} /> Sent
                  </>
                ) : (
                  <>
                    <Send size={18} /> Send Enquiry
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
